import { createContext, useContext } from 'react';
import { batch, computed, signal, useSignalEffect } from '@preact/signals-react';
import { useSearchParams } from 'next/navigation.js';
import { useEffectOnMounted } from '~/lib/hooks/mod.js';
import { PersistentStorage } from '~/lib/data/mod.storage.js';
import { getClampedFontSizePx } from '~/ui/Layout/utils.js';

import { deserializeBookmarks, serializeBookmarks } from '../Bookmarks/mod.js';

export const AppState = ({ hasRuntimeJs = false, bookmarks = {}, fontSize = null } = {}) => {
	// Default: determine whether JS exists by presence of global `document`. Can be overridden with
	// a boolean value for testing/Storybook.
	const sigHasRuntimeJs = signal(hasRuntimeJs);
	const sigBookmarkingIsEnabled = computed(() => sigHasRuntimeJs.value);

	return {
		loaded: signal(false),
		hasRuntimeJs: sigHasRuntimeJs,
		isBookmarkingEnabled: sigBookmarkingIsEnabled,
		year: signal(''),
		eventData: {
			bookmarks: signal(bookmarks),
		},
		layout: {
			fontSize: signal(fontSize == null ? fontSize : Number(fontSize)),
		},
	};
};

const AppStateContext = createContext({});
const defaultAppState = AppState();

export const AppStateProvider = ({ appState = defaultAppState, children }) => {
	useEffectOnMounted(() => {
		// Progressive enhancement to signal for JS init
		appState.hasRuntimeJs.value = true;
	});

	useEffectOnMounted(() => {
		// Begin persisted state hydration
		const eventYear = document.querySelector('meta[name="event-year"]')?.content;

		if (!eventYear) {
			console.warn('[WARN]', 'Unable to find event year in DOM. Using default app state');
			return;
		}

		const persistedData = PersistentStorage.get(`event:${eventYear}`) ?? {};
		batch(() => {
			appState.year.value = eventYear;
			if (persistedData.layout?.fontSize) {
				appState.layout.fontSize.value = getClampedFontSizePx(persistedData.layout.fontSize);
			}

			if (persistedData.eventData?.bookmarks) {
				appState.eventData.bookmarks.value = deserializeBookmarks(
					persistedData.eventData.bookmarks
				);
			}

			appState.loaded.value = true;
		});

	});

	// Write out persistable parts of `appState` whenever one of its state values change
	useSignalEffect(() => {
		const { eventData, layout, year } = appState;
		const persistedState = {
			eventData: {
				bookmarks: serializeBookmarks(eventData.bookmarks),
			},
			layout: {
				fontSize: layout.fontSize.value,
			},
		};
		PersistentStorage.set(`event:${year.value}`, persistedState);
	});

	return <AppStateContext.Provider value={appState}>{children}</AppStateContext.Provider>;
};

const reYear = /^\d{4}$/;

/**
 * Year to use for year-specific UI (logos, theme class, etc.). In non-production builds, a
 * `?year=YYYY` query param overrides it for previewing other years' UI. Never affects the
 * persisted-state key, which stays on `appState.year`.
 *
 * `appState.year` is only hydrated client-side after mount, so `fallbackYear` (the same event year
 * the page writes into its `event-year` meta tag) covers SSR and the first client render.
 *
 * NOTE: The implementation is chosen at module level (rather than branching inside the hook) so
 * the dev-only branch is dead-code eliminated from production builds and hook call order stays
 * stable within a build.
 *
 * @param {import('@preact/signals-react').Signal<string>} year The `appState.year` signal
 * @param {string} [fallbackYear] Year to use until `appState.year` is hydrated
 * @returns {string} The year to render year-specific UI for
 */
export const useDisplayYear =
	process.env.NODE_ENV === 'production' ?
		(year, fallbackYear) => year.value || fallbackYear
	:	(year, fallbackYear) => {
			const override = useSearchParams().get('year');

			return override && reYear.test(override) ? override : year.value || fallbackYear;
		};

export const withAppState = BaseComponent => {
	const WithInjectedAppState = props => {
		const appState = useContext(AppStateContext);

		return <BaseComponent {...props} {...appState} />;
	};

	return WithInjectedAppState;
};
